import React, { useState, useEffect, useMemo } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "chart.js/auto";
import connectedIcon from "../../../../assets/images/connected.svg";
import disconnectedIcon from "../../../../assets/images/disconnected.svg";
import StorageChart from "./StorageChart";
import "./Storage.css";
import { useNavigation } from "../../../../contexts/NavigationContext";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import { sysConfigApi } from "../../../../services/api/sysconfigService";
import { useTimeseriesSysconfig } from "../../hooks/useTimeseriesSysconfig";
import { listDevicesFromSysconfig } from "../../../devices/domain/deviceModel";
import { formatWithCommas } from "../../utils/formatNumber";
import {
  buildStorageDeviceFlowRows,
  deviceCardTotal,
} from "../Summary/cardFlowRows";
import CardFlowRows from "../Summary/CardFlowRows.jsx";
import { useDemandChargeData } from "../../hooks/useDemandChargeData";
import {
  buildDemandChargePayload,
  sumDeviceAttributionForPeriod,
} from "../../utils/demandCharge";

export default function Storage() {
  const { exportedStorageData } = useExportedData();
  const [deviceByType, setDeviceByType] = useState({ EV: [], BESS: [] });
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const token = sessionStorage.getItem("accessToken");
  const { timeSeriesFileName } = useNavigation();

  const accountname = sessionStorage.getItem("storageAccountName");

  const {
    microgridName,
    datasetName: resolvedDatasetName,
    loading: paramsLoading,
  } = useTimeseriesSysconfig();

  useEffect(() => {
    const email = sessionStorage.getItem("selectedCustomerEmail") ||sessionStorage.getItem("email");
    if (email) {
      setCustomer(email);
    } else {
      console.error("No customer email found in session storage.");
    }
  }, []);

  useEffect(() => {
    const fetchDevices = async () => {
      setLoading(true);
      try {
        const data = await sysConfigApi({
          accountname,
          simulationName: microgridName,
          datasetName: resolvedDatasetName,
          token,
        });

        const mapping = { EV: [], BESS: [] };
        const allDevices = listDevicesFromSysconfig(data);

        // Keep the normalized device, not just `raw`: it carries the
        // schema-derived `type` (from the sysconfig meter collection the device
        // came from) that the cards below branch on. `raw` stays reachable at
        // `.raw` for the sysconfig-shaped fields.
        allDevices
          .filter((d) => d.type === "ev")
          .forEach((d) => mapping.EV.push(d));

        allDevices
          .filter((d) => d.type === "battery")
          .forEach((d) => mapping.BESS.push(d));

        setDeviceByType(mapping);
      } catch (error) {
        console.error("Error fetching devices:", error);
        setDeviceByType({ EV: [], BESS: [] });
      } finally {
        setLoading(false);
      }
    };
    if (customer && token && resolvedDatasetName && !paramsLoading) {
      fetchDevices();
    }
  }, [customer, token, resolvedDatasetName, paramsLoading]);

  const evDevices = Array.isArray(deviceByType?.EV) ? deviceByType.EV : [];
  const bessDevices = Array.isArray(deviceByType?.BESS) ? deviceByType.BESS : [];
  const allDevicesToRender = [...evDevices, ...bessDevices];

  const bessMaxCapacity = useMemo(() => {
    return {
      BESS: deviceByType?.BESS?.map((dev) => dev.raw?.maximum_capacity_kwh) || [],
    };
  }, [deviceByType?.BESS]);

  const evMaxCapacity = useMemo(() => {
    return {
      EV: deviceByType?.EV?.map((dev) => dev.raw?.maximum_capacity_kwh) || [],
    };
  }, [deviceByType?.EV]);

  const deviceNames = useMemo(() => {
    return {
      BESS: bessDevices.map((dev) => dev?.name).filter(Boolean) || [],
      EV: evDevices.map((dev) => dev?.name).filter(Boolean) || [],
    };
  }, [bessDevices, evDevices]);

  /* ── Demand charge ────────────────────────────────────────────────────
   *
   * The site's demand charge, and the daily-summary rows the per-device
   * attribution rides on. Same hook the Summary tab uses, so the two pages
   * agree on whether this site is billed a demand charge at all.
   *
   * Must sit above the early returns below: hooks cannot be called
   * conditionally, and `noDevices` changes as the sysconfig fetch resolves. */
  const { hasDemandCharge, dailyDemand } = useDemandChargeData();

  /* The period the cards are describing, taken off the stamp StorageChart puts
   * on every metrics entry. Every entry carries the same stamp -- it is the
   * chart's own selector state, not a per-device fact -- so the first entry is
   * as good as any, and there is nothing to reconcile between devices.
   *
   * ISO keys, never the "Month: January 2040" label beside them. */
  const period = useMemo(() => {
    const first = Array.isArray(exportedStorageData)
      ? exportedStorageData[0]
      : null;
    return {
      view: first?.periodView || "",
      selDate: first?.selDate || "",
      selMonth: first?.selMonth || "",
      selYear: first?.selYear || "",
    };
  }, [exportedStorageData]);

  /* Built by the same function OverlayChart builds the Summary tab's payload
   * with, so the absence rules -- which zero is measured, when a category value
   * becomes null -- are stated once rather than copied onto this page. */
  const demandData = useMemo(
    () =>
      buildDemandChargePayload(
        hasDemandCharge,
        dailyDemand,
        period.view,
        period.selDate,
        period.selMonth,
        period.selYear,
      ),
    [hasDemandCharge, dailyDemand, period],
  );

  const noDevices =
  !loading &&
  evDevices.length === 0 &&
  bessDevices.length === 0;

if (noDevices) {
  return (
    <div className="storage-container">
      <div className="sticky-header">
        <h1>Storage</h1>
      </div>
      <div className="no-results">
        <p>No devices available</p>
      </div>
    </div>
  );
}

  const handleLoadingStateChange = (loading) => {
    setIsChartLoading(loading);
  };

  const StatsCardsSkeleton = () => (
    <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
      <div className="sticky-cards-storage">
        <div className="card-container-storage">
          <div className="stats-container-storage storage-skeleton-card">
            <Skeleton width={120} height={24} style={{ marginBottom: "8px" }} />
            <Skeleton
              width={150}
              height={18}
              style={{ marginBottom: "20px" }}
            />
            <Skeleton width={80} height={16} style={{ marginBottom: "8px" }} />
            <div className="prod-cost-display">
              <Skeleton width={180} height={48} />
            </div>
            <div className="device-stats-storage" style={{ marginTop: "20px" }}>
              <div className="stat-item-storage">
                <Skeleton
                  width={80}
                  height={16}
                  style={{ marginBottom: "4px" }}
                />
                <Skeleton width={60} height={20} />
              </div>
              <div className="stat-item-storage">
                <Skeleton
                  width={90}
                  height={16}
                  style={{ marginBottom: "4px" }}
                />
                <Skeleton width={70} height={20} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );

  const getMetricsByDevice = (deviceName) => {
    const metricsArray = Array.isArray(exportedStorageData) ? exportedStorageData : [];
    const found = metricsArray.find((m) => m.deviceName === deviceName);

    if (!found) {
      return {
        view: "",
        selectedPeriod: "",
        value: "0",
        maxPower: "0",
        totalEnergy: "0",
        chargeMax: "0",
        dischargeMax: "0",
        chargeTotal: "0",
        dischargeTotal: "0",
        avgSOC: "0",
        // No metrics yet is not the same as "drove 0 kWh" — keep it absent.
        hasConsumed: false,
        consumedMax: null,
        consumedTotal: null,
        // Same reasoning for the engine cost columns: no metrics means unknown.
        operatingCost: null,
        arbitrage: null,
        // And for the Losses row. "No worker result yet" is not "lost nothing".
        hasLosses: false,
        lossesMax: null,
        lossesTotal: null,
        lossCost: null,
        deviceName,
      };
    }
  
    return found;
  };


  return (
    <div className="storage-container">
      <div className="sticky-header">
        <h1>Storage</h1>
      </div>

      {isChartLoading || loading ? (
        <StatsCardsSkeleton />
      ) : (
        <>
          {allDevicesToRender.length > 0 && (
            <div className="sticky-cards-storage">
             <div
  className={`card-container-storage ${
    allDevicesToRender.length > 3 ? "scrollable-cards" : ""
  }`}
>
                {allDevicesToRender.map((device, deviceIndex) => {
                  const deviceName = (device?.name ?? "Unknown Device").toString();
                  const isConnected = "connected";

                  // Schema-derived device type from listDevicesFromSysconfig
                  // ("ev" = the meter's `evs` collection). Distinct from the
                  // worker's `type` argument, which is only a CSV column token.
                  const isEV = device?.type === "ev";

                  const metrics = getMetricsByDevice(deviceName);
                  const { view, selectedPeriod, value, avgSOC } = metrics;

                  const formattedAvgSOC = formatWithCommas(avgSOC);

                  /* This device's share of the monthly demand charge, over the
                   * SAME period the energy figures beside it cover -- the
                   * chart's ISO keys, sliced by the same predicate the summary
                   * card uses.
                   *
                   * The daily summary's sign convention travels untouched: a
                   * cost negative, a benefit positive. A battery that pulls the
                   * peak down reads POSITIVE (Battery1 is +334,627.12 over
                   * husky-2040-prod's year) while an EV group that pushes it up
                   * reads negative. That is the same quantity, with the same
                   * sign, that the Summary tab's Storage card shows summed
                   * across devices; flipping it here to "look like a cost"
                   * would put the two pages in contradiction.
                   *
                   * null -- absent, never 0 -- when this run's daily summary
                   * carries no column for this device, or when the chart has
                   * not resolved a period yet. Stringified at two decimals so
                   * every money figure on the card takes one rounding path.
                   *
                   * Not memoised: it is one pass over ~366 daily rows per
                   * device, and hooks cannot be called from inside this map. */
                  const rawDeviceDemand = sumDeviceAttributionForPeriod(
                    dailyDemand,
                    deviceName,
                    period.view,
                    period.selDate,
                    period.selMonth,
                    period.selYear,
                  );
                  const source = {
                    ...metrics,
                    deviceDemand:
                      rawDeviceDemand === null
                        ? null
                        : rawDeviceDemand.toFixed(2),
                  };

                  /* One row per flow, each carrying that flow's power, energy
                   * and money. Built by the module the summary cards use, so
                   * the two surfaces cannot drift on what a row means or on
                   * what an absent figure looks like. */
                  const flowRows = buildStorageDeviceFlowRows(source, {
                    isEV,
                    demandData,
                  });

                  /* The card's NET: the engine's Storage Cost for this device
                   * plus this device's demand attribution. Two named figures,
                   * never a sum of the rows on screen. */
                  const { total, includesDemand } = deviceCardTotal(
                    value,
                    source,
                    demandData,
                  );

                  let fillLevel = Math.ceil((Number(avgSOC) / 100) * 10);
                  if (fillLevel > 10) fillLevel = 10;

                  return (
                    <div key={deviceName} className="card storage-card">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center">
                            <h5
                              className="card-title mb-0 device-name-storage"
                              style={{
                                cursor: "pointer",
                                maxWidth: "200px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={deviceName.length > 25 ? deviceName : ""}
                            >
                              {deviceName.length > 25
                                ? `${deviceName.substring(0, 25)}...`
                                : deviceName}
                            </h5>
                            <img
                              src={isConnected ? connectedIcon : disconnectedIcon}
                              alt={isConnected ? "Connected" : "Disconnected"}
                              className="connection-icon ms-2"
                            />
                          </div>
                          <div className="soc-storage">
                            Avg SOC: <strong>{formattedAvgSOC}%</strong>
                          </div>
                          <div className="box-transfer">
                            <span className="period-storage">
                              {view == "All Time View"
                                ? "All Time View"
                                : `${view}: ${selectedPeriod}`}
                            </span>
                          </div>
                        </div>

                        <div className="d-flex justify-content-center my-2">
                          <div className="battery-indicator">
                            <div className="battery-shell">
                              {Array.from({ length: 10 }).map((_, idx) => (
                                <div
                                  key={idx}
                                  className={`battery-section ${
                                    idx < fillLevel ? "filled" : "empty"
                                  }`}
                                  style={{
                                    backgroundColor:
                                      idx < fillLevel
                                        ? Number(avgSOC) <= 10
                                          ? "#FF0000"
                                          : Number(avgSOC) <= 40
                                            ? "#FFD700"
                                            : "#008000"
                                        : "#E0E0E0",
                                  }}
                                ></div>
                              ))}
                            </div>
                            <div className="battery-tip"></div>
                          </div>
                        </div>

                        <CardFlowRows
                          rows={flowRows}
                          total={total}
                          totalIncludesDemand={includesDemand}
                          testIdPrefix={`storage-device-${deviceIndex}`}
                          showColumnHeaders
                        />

                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
      <StorageChart
        csvData={timeSeriesFileName}
        bessMaxCapacity={bessMaxCapacity}
        evMaxCapacity={evMaxCapacity}
        deviceNames={deviceNames}
        onLoadingStateChange={handleLoadingStateChange}
      />
    </div>
  );
}
